import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {memo, useMemo, useState} from 'react';
import {Column} from 'react-table';

import {useTeamMemberRolesQuery} from '../../../generated/graphql';
import {MimicTable} from '../../../pages/Team/MimicTable';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import * as urls from '../../../util/urls';
import {TeamMemberRole} from '../../MembersTable/TeamMembersTable/utils';
import {RoleOption} from '../../MembersTable/TeamOrOrganizationRoleOption';
import WBReactTable from '../../WBReactTable';
import {AddProjectMembers} from './AddProjectMembers';
import {DeleteProjectMemberModal} from './DeleteProjectMemberModal';
import {
  getProjectRoleOptionFromRoleOption,
  ProjectMembersRoleCell,
} from './ProjectMembersRoleCell';
import {
  getTeamProjectMembers,
  ProjectRoleMember,
  TeamProjectMember,
} from './utils';

type ProjectMembersTableProps = {
  projectMembers: ProjectRoleMember[];
  projectID: string;
  projectName: string;
  projectOwnerID: string;
  projectTeamName: string;
  roleOptions: RoleOption[];
  projectAccess: AccessOption;
};

export const ProjectMembersTable = memo(
  ({
    projectID,
    projectName,
    projectOwnerID,
    projectTeamName,
    projectMembers,
    roleOptions,
    projectAccess,
  }: ProjectMembersTableProps) => {
    const viewer = useViewer();
    const [isDeleteMemberModalOpen, setIsDeleteMemberModalOpen] =
      useState(false);
    const [memberToDelete, setMemberToDelete] = useState<
      TeamProjectMember | undefined
    >();
    const handleDeleteModalClose = () => {
      setMemberToDelete(undefined);
      setIsDeleteMemberModalOpen(false);
    };

    const {data: teamData, loading: isTeamMembersQueryLoading} =
      useTeamMemberRolesQuery({
        variables: {entityName: projectTeamName},
        context: propagateErrorsContext(),
      });

    const teamProjectMembers = getTeamProjectMembers(
      teamData?.entity?.members ?? [],
      projectMembers ?? []
    );

    const teamProjectMembersMap = new Map<string, TeamProjectMember>();
    teamProjectMembers.forEach(member =>
      teamProjectMembersMap.set(member.id, member)
    );

    // Get viewer's team role
    const viewerTeamRole = teamProjectMembersMap.get(
      viewer?.id ?? ''
    )?.teamRole;

    const isViewerProjectAdmin =
      teamProjectMembersMap.get(viewer?.id ?? '')?.role ===
      TeamMemberRole.ADMIN;
    const isViewerTeamAdmin = viewerTeamRole === TeamMemberRole.ADMIN;
    // viewer can edit self if they are a team admin or a non-view-only project owner
    const canViewerEditSelf =
      isViewerTeamAdmin ||
      (viewer?.id === projectOwnerID &&
        viewerTeamRole !== TeamMemberRole.VIEW_ONLY);

    const projectRoleOptions = roleOptions.map(opt =>
      getProjectRoleOptionFromRoleOption(opt)
    );

    const columns = useMemo(() => {
      const baseColumns: Array<Column<TeamProjectMember>> = [
        {
          Header: 'PROFILE',
          accessor: 'profile',
          Cell: ({original}) => {
            const user: TeamProjectMember = original;
            return (
              <div className="flex items-center gap-8">
                <img
                  src={user.photoUrl ?? '/default-profile-picture.png'}
                  className="h-36 w-36 rounded-full"
                  alt=""
                />
                <div>
                  <p className="font-semibold">{user.name}</p>
                  {user.username && (
                    <TargetBlank
                      className="text-sm text-teal-600"
                      href={urls.profilePage(user.username)}>
                      @{user.username}
                    </TargetBlank>
                  )}
                </div>
              </div>
            );
          },
        },
        {
          Header: 'EMAIL',
          accessor: 'email',
          Cell: ({original}) => {
            const user: TeamProjectMember = original;
            return (
              <div className="flex h-full items-center">
                <p className="break-words">{user.email ?? '-'}</p>
              </div>
            );
          },
        },
        {
          Header: 'PROJECT ROLE',
          accessor: 'role',
          Cell: ({original}) => {
            const user: TeamProjectMember = original;
            const isSelfRole = user.id === viewer?.id;
            return (
              <ProjectMembersRoleCell
                projectRoleOptions={projectRoleOptions}
                canViewerPerformActions={
                  isViewerProjectAdmin || (isSelfRole && canViewerEditSelf)
                }
                projectID={projectID}
                projectName={projectName}
                projectOwnerID={projectOwnerID}
                projectTeamName={projectTeamName}
                projectMemberUser={user}
              />
            );
          },
        },
      ];

      if (isViewerProjectAdmin && projectAccess === AccessOption.Restricted) {
        baseColumns.push({
          Header: ' ',
          accessor: 'actions',
          sortable: false,
          Cell: ({original}) => {
            const user: TeamProjectMember = original;
            if (user.id !== projectOwnerID) {
              return (
                <div className="flex h-full items-center justify-end gap-8">
                  {' '}
                  <Button
                    variant="ghost"
                    icon="delete"
                    onClick={() => {
                      setIsDeleteMemberModalOpen(true);
                      setMemberToDelete(user);
                    }}
                  />
                </div>
              );
            }
            return <div />;
          },
        });
      }

      return baseColumns;
    }, [
      canViewerEditSelf,
      isViewerProjectAdmin,
      projectAccess,
      projectID,
      projectName,
      projectOwnerID,
      projectRoleOptions,
      projectTeamName,
      viewer?.id,
    ]);

    const memberRows =
      teamProjectMembers.map(member => {
        const {name, username, email, role} = member;

        return {
          searchString: `${name}${username}${email}${role}`,
          row: {...member, displayName: name, actions: {member}},
        };
      }) ?? [];

    if (isTeamMembersQueryLoading) {
      return <MimicTable columnNames={['NAME', 'ROLE', 'EMAIL']} numRows={5} />;
    }

    return (
      <Tailwind style={{paddingBottom: '100px'}}>
        <div className="mb-8 flex items-center justify-between gap-8">
          <div className="project-users-count">
            {projectMembers.length} Users
          </div>
          {projectAccess === AccessOption.Restricted &&
            isViewerProjectAdmin && (
              <div className="flex items-center gap-8">
                <AddProjectMembers
                  projectID={projectID}
                  projectName={projectName}
                  projectTeamName={projectTeamName}
                  projectMembers={projectMembers}
                  projectAccess={projectAccess}
                />
              </div>
            )}
        </div>
        <WBReactTable
          className="rounded-lg border-moon-250"
          data-test="project-members-table"
          columns={columns}
          data={memberRows}
          defaultSorted={[{id: 'email', desc: true}]}
          pageSize={10}
        />
        {memberToDelete != null && (
          <DeleteProjectMemberModal
            isOpen={memberToDelete != null && isDeleteMemberModalOpen}
            handleModalClose={handleDeleteModalClose}
            member={memberToDelete}
            projectID={projectID}
            projectName={projectName}
            projectTeamName={projectTeamName}
            setIsModalOpen={setIsDeleteMemberModalOpen}
          />
        )}
      </Tailwind>
    );
  }
);
